import {
    GetCaregiverDetailsForHcsResponseIds,
    HcsDelegateRoleRef2Ids,
    HcsPositionRefIds,
    useGetIdsHealthcareservicesHcsIdCaregiversUserId,
    usePutIdsHealthcareservicesHcsIdCaregiversUserId,
} from '@toktokdoctech/tokapi-client'
import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { defaultValues, UserFormContextProvider } from './utils/userFormContext'
import { UserFormErrorContextProvider, defaultUserErrorValues } from './utils/userFormErrorContext'
import { RouterRouteId } from '../../routers/_props'
import useCurrentHcs from '../../hooks/useCurrentHcs'
import { CaregiverForm } from './CaregiverForm'
import { UserLoader } from './UserLoader'
import { UserData } from './_props'
import { buildCommonFields } from './utils/addOrUpdateUser/updatePayloadBuilders'

export const CaregiverEdition = () => {
    const currentHcs = useCurrentHcs()
    const params = useParams<RouterRouteId>()
    const userId = params.id

    const putCaregiver = usePutIdsHealthcareservicesHcsIdCaregiversUserId()
    const { data, isLoading } = useGetIdsHealthcareservicesHcsIdCaregiversUserId(currentHcs.id, userId ?? '', {
        query: { enabled: !!userId },
    })

    const transformUserData = (caregiver: GetCaregiverDetailsForHcsResponseIds): UserData => {
        const profession = caregiver.professions?.at(0) ?? { id: '', name: '' }

        return {
            objectId: caregiver.id,
            firstName: caregiver.firstName,
            lastName: caregiver.lastName,
            civility: caregiver.civility,
            title: caregiver.prefix,
            rpps: caregiver.rppsNum ?? undefined,
            validRpps: !!(caregiver.rppsNum ?? caregiver.adeliNum),
            category: caregiver.category,
            profession: profession.name,
            professionId: profession.id,
            qualification: caregiver.qualification?.name ?? '',
            qualificationId: caregiver.qualification?.id,
            speciality: caregiver.speciality?.name,
            specialityId: caregiver.speciality?.id,
            positionName: caregiver.position?.name,
            positionId: caregiver.position?.id,
            role: {
                id: caregiver.role?.id ?? '',
                name: caregiver.role?.name ?? '',
                code: caregiver.role?.code,
            },
            speMentions: caregiver.mentions.map((mention) => ({ id: mention.id, name: mention.name })),
            idnps: caregiver.nationalId || undefined,
            email: caregiver.username,
            initialEmail: caregiver.username,
            securedEmail: caregiver.securedEmail ?? undefined,
            privatePhone: caregiver.privatePhone ?? undefined,
            contactPhone: caregiver.contactPhone ?? undefined,
            isReal: caregiver.isReal,
            activated: caregiver.isEnabled,

            extraHealthActivityInfoId: caregiver.extraHealthActivityInfoId || undefined,
            bankInfoObjectId: caregiver.bankInfoObjectId ?? '',
            addressObjectId: caregiver.addressObjectId || undefined,
            hcsDelegateUserId: caregiver.hcsDelegateUserId || undefined,
            userHcsPositionId: caregiver.userHcsPositionId || undefined,
            extraUsageInfoObjectId: caregiver.userUsageInfoId ?? '',
            extraHealthSystemInfoId: caregiver.userHealthSystemInfoId ?? '',
        }
    }

    const transformedData = useMemo(() => (data ? transformUserData(data) : undefined), [data])

    const handleSave = useCallback(
        async (userData: UserData) => {
            const { identity, rppsNum, adeliNum, professions, qualification, speciality, speMentions } =
                buildCommonFields(userData)

            const position: HcsPositionRefIds = userData.positionId
                ? { id: userData.positionId, name: userData.positionName ?? '' }
                : null

            const role: HcsDelegateRoleRef2Ids = userData.role?.id
                ? { id: userData.role.id, name: userData.role.name ?? '' }
                : null

            const payload = {
                ...identity,
                rppsNum: rppsNum ?? undefined,
                adeliNum: adeliNum ?? undefined,
                professions,
                qualification,
                speciality,
                speMentions: speMentions.length > 0 ? speMentions : undefined,
                position,
                category: userData.category ?? null,
                contactPhone: userData.contactPhone ?? null,
                // Backend only allows emptying privatePhone (not modifying). Send the current
                // value as a no-op so a reset ('') propagates while any edit attempt is rejected.
                privatePhone: userData.privatePhone ?? null,
                role,
                isEnabled: userData.activated ?? true,
            }

            await putCaregiver.mutateAsync({ hcsId: currentHcs.id, userId: userId ?? '', data: payload })
        },
        [currentHcs.id, putCaregiver, userId],
    )

    return (
        <UserFormContextProvider initialValues={defaultValues}>
            <UserFormErrorContextProvider initialValues={defaultUserErrorValues}>
                {isLoading && <UserLoader />}
                {!isLoading && transformedData && (
                    <CaregiverForm mode="EDITION" data={transformedData} onSave={handleSave} />
                )}
            </UserFormErrorContextProvider>
        </UserFormContextProvider>
    )
}
